import { forwardRef, useMemo } from 'react';
import { Cluster, hierarchy } from '@visx/hierarchy';
import { LinkHorizontal as VisxLinkHorizontal } from '@visx/shape';

import { useTheme } from '@simplywallst/ui-core';

import { Ticker, Industry, Fork, Root } from '../NodeLabels';
import { Label } from '../Label';
import { Trunk } from '../Trunk';

import type { NormalisedDiversification } from '../../hooks/useNormalisedDiversification';

import type {
  PortfolioDiversification,
  HierarchyPointLink,
  HierarchyPointNode,
} from '../../types';

import {
  sortByWeight,
  collapseChildren,
  getBranchWidth,
  getSource,
  getLabelDepths,
  MOBILE_TRUNK_DEPTH,
  getPaddingTop,
  getVerticalNodeSpacing,
} from '../../utils';

const columnLabels = ['Industry', 'Ticker'] as const;

interface Props {
  data: NormalisedDiversification;
  holdingsCount: number;
  width: number;
  onLabelClick?: (labelType: (typeof columnLabels)[number]) => void;
}

export const MobileCluster = forwardRef<SVGGElement, Props>(
  ({ data, holdingsCount, width, onLabelClick }, ref) => {
    const theme = useTheme();
    const marginRight = 70;
    const xMax = width - marginRight;

    const verticalNodeSpacing = getVerticalNodeSpacing(holdingsCount, theme);
    const paddingTop = getPaddingTop(holdingsCount, theme);

    const nodeSize: [number, number] = useMemo(
      () => [verticalNodeSpacing, xMax / columnLabels.length],
      [xMax, verticalNodeSpacing]
    );

    const hierarchyNode = useMemo(() => {
      return hierarchy<PortfolioDiversification>(collapseChildren(data)).sort(
        sortByWeight
      );
    }, [data]);

    return (
      <g ref={ref}>
        <Cluster<PortfolioDiversification>
          root={hierarchyNode}
          nodeSize={nodeSize}
        >
          {(cluster) => {
            const columns = getLabelDepths(cluster);
            const terminalDepth = Math.max(...columns.map((n) => n.depth));

            // The nodes returned in columns also represent the top most nodes
            // We can use these to understand the upmost pixel value of the chart in "chart-space"
            const top = Math.min(...columns.map((n) => n.x)) - paddingTop;

            return (
              <g transform={`translate(0,${Math.abs(top)})`}>
                <Trunk node={cluster} width={MOBILE_TRUNK_DEPTH} />
                {cluster.links().map((link, i) => {
                  let opacity = 0.5;

                  if (link.source.depth === 0) {
                    opacity = 0.9;
                  }

                  return (
                    <VisxLinkHorizontal<HierarchyPointLink, HierarchyPointNode>
                      key={`cluster-link-${i}`}
                      data-cy-id="branch"
                      data={link}
                      stroke={theme.color.brand01RGBA(opacity)}
                      strokeWidth={getBranchWidth(link.target.data.weight)}
                      source={(link) => getSource(link, MOBILE_TRUNK_DEPTH)}
                      fill="none"
                    />
                  );
                })}
                {cluster.descendants().map((node, i) => {
                  const key = `cluster-node-${i}`;
                  if (node.depth === 0) {
                    return <Root key={key} node={node} />;
                  }
                  if (node.depth === 1) {
                    if (node.depth === terminalDepth) {
                      return (
                        <Industry
                          key={key}
                          node={node}
                          onClick={() => onLabelClick?.('Industry')}
                        />
                      );
                    }
                    return <Fork key={key} node={node} />;
                  }
                  return <Ticker key={key} node={node} />;
                })}

                {columns.map((column, i) => (
                  <Label key={`column-label-${i}`} left={column.y} top={top}>
                    {columnLabels[i]}
                  </Label>
                ))}
              </g>
            );
          }}
        </Cluster>
      </g>
    );
  }
);

MobileCluster.displayName = 'MobileCluster';
